import { useCallback, useEffect, useRef, useState } from 'react';

import { deleteShopOrderById } from '@shared/api/shopOrder/shopOrder.controller';
import { confirmTossPayment } from '@shared/api/tosspayments/tosspayments.controller';
import { toastError } from '@shared/plugIn/toast';
import {
  parseFailResult,
  parseSuccessResult,
} from '@shared/plugIn/tosspayments/parsePaymentResult';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';

export interface ConfirmError {
  title: string;
  message: string;
  orderId: string;
  /** 재시도 가능 여부 (success redirect에서만 true) */
  canRetry: boolean;
}

export const MAX_RETRY = 3;

interface UseTossPaymentResultParams {
  bugoId: string | null;
  onRestartPayment?: () => void;
  onProviderUnavailable?: () => void;
}

/**
 * 토스페이먼츠 redirect 결과를 처리하는 훅
 *
 * 결제 흐름:
 * 1. 토스 SDK requestPayment → 토스 결제창 → 인증 완료 → redirect (successUrl / failUrl)
 * 2. success redirect → confirmTossPayment(서버 승인) → 성공 페이지 이동
 *    - 승인 실패 시 자동 1회 재시도 후 에러 오버레이 노출
 *    - 사용자 수동 재시도 최대 3회 (MAX_RETRY)
 * 3. fail redirect → 주문 삭제 → 에러 토스트 → 체크아웃 페이지 복귀
 */
export const useTossPaymentResult = ({
  bugoId,
  onRestartPayment,
  onProviderUnavailable,
}: UseTossPaymentResultParams) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { storeRole } = useAuth();

  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<ConfirmError | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const processedRef = useRef(false);
  const confirmedRef = useRef(false);

  // 결제 승인 중 이탈 방지
  useEffect(() => {
    if (!isConfirming) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isConfirming]);

  // 결제 승인 처리 (success redirect, 실패 시 자동 1회 재시도)
  const processSuccess = useCallback(
    async (orderId: string, paymentKey: string, amount: number) => {
      setIsConfirming(true);
      setConfirmError(null);

      const AUTO_RETRY = 1;
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= AUTO_RETRY; attempt++) {
        try {
          if (!confirmedRef.current) {
            // 테스트 플래그: localStorage에 '__TEST_PROVIDER_UNAVAILABLE__' 설정 시 PROVIDER_UNAVAILABLE 시뮬레이션
            if (localStorage.getItem('__TEST_PROVIDER_UNAVAILABLE__')) {
              const err: any = new Error('PROVIDER_UNAVAILABLE'); // eslint-disable-line @typescript-eslint/no-explicit-any
              err.response = { data: { error: { code: 'PROVIDER_UNAVAILABLE' } } };
              throw err;
            }
            await confirmTossPayment({ paymentKey, orderId, amount });
            confirmedRef.current = true;
          }
          navigate(
            `/flower-store/order/${orderId}/payment/?success=true&bugoId=${bugoId}`,
            { replace: true },
          );
          return;
        } catch (err) {
          lastError = err as Error;
          const errorCode = (err as any)?.response?.data?.error?.code;

          // PROVIDER_UNAVAILABLE → 부트페이 폴백 콜백에 위임
          if (errorCode === 'PROVIDER_UNAVAILABLE') {
            setIsConfirming(false);
            onProviderUnavailable?.();
            return;
          }

          if (attempt < AUTO_RETRY) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }
      }

      setIsConfirming(false);
      setConfirmError({
        title: '결제 승인 실패',
        message: lastError?.message || '결제 승인 중 오류가 발생했습니다.',
        orderId,
        canRetry: true,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, bugoId, onProviderUnavailable],
  );

  // useTossPaymentResult.ts 훅 참조
  const paymentResult = searchParams.get('result') as
    | 'success'
    | 'fail'
    | null
    | undefined;

  // 토스페이먼츠 redirect 결과 처리
  useEffect(() => {
    if (!paymentResult || processedRef.current) return;
    processedRef.current = true;

    if (paymentResult === 'success') {
      const { paymentKey, orderId, amount } = parseSuccessResult(searchParams);
      if (!paymentKey || !orderId) {
        navigate('/flower-store', { replace: true });
        return;
      }
      processSuccess(orderId, paymentKey, amount);
    } else if (paymentResult === 'fail') {
      const { orderId, message } = parseFailResult(searchParams);

      const handleFail = async () => {
        if (orderId) {
          try {
            await deleteShopOrderById(orderId, storeRole());
          } catch {
            // 삭제 실패 시 서버 스케줄러가 정리
          }
        }
        toastError(`결제 실패${message ? `\n${message}` : ''}`);
        const cleanUrl = bugoId ? `?bugoId=${bugoId}` : window.location.pathname;
        navigate(cleanUrl, { replace: true });
      };

      handleFail();
    }
  }, [paymentResult, searchParams, navigate, storeRole, bugoId, processSuccess]);

  // 상태 초기화 (onRestartPayment에서 호출)
  const resetResult = useCallback(() => {
    processedRef.current = false;
    confirmedRef.current = false;
    setRetryCount(0);
    setConfirmError(null);
    setIsConfirming(false);
  }, []);

  // 오버레이 버튼 핸들러
  const handleRetry = useCallback(async () => {
    if (!confirmError) return;

    if (retryCount < MAX_RETRY) {
      setRetryCount((prev) => prev + 1);
      const { paymentKey, orderId, amount } = parseSuccessResult(searchParams);
      await processSuccess(orderId, paymentKey, amount);
    } else {
      onRestartPayment?.();
    }
  }, [confirmError, retryCount, searchParams, processSuccess, onRestartPayment]);

  const handleCancelOrder = useCallback(() => {
    if (!confirmError) return;
    // 주문 삭제 (fire-and-forget)
    deleteShopOrderById(confirmError.orderId, storeRole()).catch(() => {
      // 삭제 실패 시 서버 스케줄러가 정리
    });
    // 오버레이 닫기
    setConfirmError(null);
    // URL 정리 (result/paymentKey 파라미터 제거)
    const cleanUrl = bugoId ? `?bugoId=${bugoId}` : window.location.pathname;
    navigate(cleanUrl, { replace: true });
  }, [confirmError, storeRole, navigate, bugoId]);

  return {
    isConfirming,
    confirmError,
    retryCount,
    handleRetry,
    handleCancelOrder,
    resetResult,
  };
};
